import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { Mail, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';

import { FORGOT_PASSWORD_MUTATION } from '../services/graphql/mutations.js';
import { addAlert } from '../store/slices/uiSlice.js';

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // GraphQL mutation
  const [forgotPasswordMutation] = useMutation(FORGOT_PASSWORD_MUTATION);

  // Validation
  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de correo electrónico inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { data, errors } = await forgotPasswordMutation({
        variables: {
          input: {
            email: formData.email.toLowerCase().trim(),
          },
        },
        errorPolicy: 'all',
      });

      // Check if we have GraphQL errors but no data
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }

      // Check if data is null or doesn't have forgotPassword
      if (!data || !data.forgotPassword) {
        throw new Error('Error al procesar solicitud. Intente nuevamente.');
      }

      const { success, message } = data.forgotPassword;

      if (success) {
        setIsSubmitted(true);
        
        dispatch(addAlert({
          type: 'success',
          title: 'Contraseña Restablecida',
          message: message || 'Se ha enviado una contraseña temporal a su correo electrónico',
        }));
      }

    } catch (error) {
      console.error('Forgot password error:', error);
      
      let errorMessage = 'Error al procesar solicitud. Intente nuevamente.';
      
      // Check if it's a GraphQL error
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage = 'Error de conexión. Intente nuevamente.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Check for specific error types after initial errorMessage assignment
      if (errorMessage.includes('No se encuentra registrado') || 
          errorMessage.includes('inactivo')) {
        // Error message as per Login.md
        errorMessage = 'No se encuentra registrado en el sistema o su usuario está inactivo, favor contactar al administrador';
      }
      
      dispatch(addAlert({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  // Success view after submission
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-success-100 mb-6">
              <CheckCircle className="h-8 w-8 text-success-600" />
            </div>
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">
              Contraseña Temporal Enviada
            </h1>
            <p className="text-secondary-600">
              Revise su correo electrónico para encontrar su nueva contraseña temporal
            </p>
          </div>

          <div className="card">
            <div className="card-body text-center space-y-4">
              <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                <p className="text-sm text-success-700">
                  <strong>¡Enviado!</strong> Hemos enviado una contraseña temporal de 10 caracteres a su correo electrónico.
                </p>
              </div>

              <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                <p className="text-sm text-warning-700">
                  <strong>Importante:</strong> Por razones de seguridad, le recomendamos cambiar esta contraseña temporal 
                  tan pronto como inicie sesión en el sistema.
                </p>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-secondary-600">
                  La contraseña temporal es válida inmediatamente. Si no encuentra el correo en su bandeja de entrada, 
                  revise la carpeta de spam o correo no deseado.
                </p>

                <button
                  onClick={handleBackToLogin}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Volver al Login
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-xs text-secondary-500">
              © 2025 ACTrack. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Form view
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            Olvidó su contraseña?
          </h1>
          <p className="text-secondary-600">
            Ingrese su correo electrónico para recibir una contraseña temporal
          </p>
        </div>

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email field */}
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Correo electrónico
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`form-input pl-10 ${errors.email ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                    placeholder="Digite su correo electrónico"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                {errors.email && (
                  <div className="flex items-center mt-1 text-error-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="form-error">{errors.email}</span>
                  </div>
                )}
              </div>

              {/* Information note */}
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                <p className="text-sm text-secondary-700">
                  <strong>Importante:</strong> Solo los usuarios activos registrados en el sistema pueden recibir 
                  contraseñas temporales. Si su usuario está inactivo, contacte al administrador.
                </p>
              </div>

              {/* Instructions */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-primary-900 mb-2">¿Qué sucederá?</h4>
                <ul className="text-sm text-primary-800 space-y-1">
                  <li>• Verificaremos que su usuario existe y está activo</li>
                  <li>• Generaremos una contraseña temporal de 10 caracteres</li>
                  <li>• La enviaremos a su correo electrónico registrado</li>
                  <li>• Podrá usarla inmediatamente para iniciar sesión</li>
                </ul>
              </div>

              {/* Action buttons */}
              <div className="flex space-x-4">
                {/* Back button */}
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="btn-outline flex-1 flex items-center justify-center"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Volver
                </button>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex-1 flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    'Enviar Contraseña'
                  )}
                </button>
              </div>

              {/* Help text */}
              <div className="text-center">
                <p className="text-sm text-secondary-600">
                  ¿Recuerda su contraseña?{' '}
                  <Link
                    to="/login"
                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                  >
                    Inicie sesión aquí
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-secondary-500">
            © 2025 ACTrack. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;